import React, { useState } from 'react';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PRODUCTS = [
  { id: 'starter', name: '入門套餐', price: '$4.99', readings: 5, description: '5 次命盤解讀' },
  { id: 'value', name: '超值套餐', price: '$9.99', readings: 15, description: '15 次命盤解讀', popular: true },
  { id: 'pro', name: '專業套餐', price: '$24.99', readings: 50, description: '50 次命盤解讀' },
  { id: 'monthly', name: '月度訂閱', price: '$6.99/月', readings: 10, description: '每月 10 次解讀', subscription: true },
];

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handlePurchase = async (productId: string) => {
    setLoading(productId);
    setError(null);

    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ productId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Checkout failed');
      }

      // Redirect to Stripe checkout
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : '結帳失敗');
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-slate-700">
          <h2 className="text-xl font-serif text-amber-500">獲取更多解讀次數</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-300"
          >
            ✕
          </button>
        </div>

        {/* Products Grid */}
        <div className="p-4 grid gap-4 sm:grid-cols-2">
          {PRODUCTS.map((product) => (
            <div
              key={product.id}
              className={`relative p-4 rounded-lg border ${
                product.popular
                  ? 'border-amber-500 bg-slate-800/80'
                  : 'border-slate-600 bg-slate-800/40'
              }`}
            >
              {product.popular && (
                <span className="absolute -top-2 left-4 px-2 py-0.5 text-xs font-medium bg-amber-500 text-black rounded">
                  最受歡迎
                </span>
              )}
              <h3 className="text-lg font-medium text-slate-200">{product.name}</h3>
              <p className="text-2xl font-bold text-amber-400 my-2">{product.price}</p>
              <p className="text-slate-400 text-sm mb-4">{product.description}</p>
              <button
                onClick={() => handlePurchase(product.id)}
                disabled={loading !== null}
                className={`w-full py-2 rounded-lg font-medium transition ${
                  loading === product.id
                    ? 'bg-slate-700 text-slate-400 cursor-wait'
                    : 'bg-amber-600 hover:bg-amber-500 text-white'
                } disabled:opacity-50`}
              >
                {loading === product.id ? '處理中...' : '購買'}
              </button>
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <p className="px-4 pb-4 text-red-400 text-sm text-center">{error}</p>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 text-center text-slate-600 text-xs">
          安全付款由 Stripe 提供
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
